import { useState, useEffect } from 'react';
import moment from 'moment';
import useEmployeeContractsService from 'domain/people/employee/contracts/shared/employeeContractsService';
import { isB2BContract, isPolishOffice } from 'domain/shared/employee';
import { HANDBOOK_PATH } from 'domain/shared/core/sharedRouteConstants';

interface Contract {
  officeId: number;
  type: string;
  startDate: string;
  endDate?: string;
}

const findActiveContract = (contracts: Contract[]): Contract | null => {
  if (!contracts?.length) return null;

  const now = moment();

  return (
    contracts.find(contract => {
      const startDate = moment(contract.startDate);
      const endDate = contract.endDate ? moment(contract.endDate) : null;

      return startDate.isSameOrBefore(now, 'day') && (!endDate || endDate.isSameOrAfter(now, 'day'));
    }) || null
  );
};

const findMostRecentContract = (contracts: Contract[]): Contract | null => {
  if (!contracts?.length) return null;

  return contracts.reduce((mostRecent, current) => {
    const currentEndDate = current.endDate ? moment(current.endDate) : moment();
    const mostRecentEndDate = mostRecent.endDate ? moment(mostRecent.endDate) : moment();

    return currentEndDate.isAfter(mostRecentEndDate) ? current : mostRecent;
  });
};

const getLeavePolicyPath = (contract: Contract): string => {
  const officeId = String(contract.officeId);
  const contractType = contract.type;

  if (isPolishOffice(officeId)) {
    return isB2BContract(contractType)
      ? `${HANDBOOK_PATH}/leave-policy-poland-b2b.md`
      : `${HANDBOOK_PATH}/leave-policy-poland.md`;
  }

  return `${HANDBOOK_PATH}/leave-policy-belarus.md`;
};

const useLeavePolicyUrl = (employeeId: string) => {
  const { getAll: getAllContracts } = useEmployeeContractsService();
  const [leavePolicyUrl, setLeavePolicyUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const determineLeavePolicyUrl = async () => {
      if (!employeeId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const { result: contracts } = await getAllContracts(employeeId);
        const activeContract = findActiveContract(contracts);
        const contractToUse = activeContract || findMostRecentContract(contracts);

        if (contractToUse) {
          setLeavePolicyUrl(getLeavePolicyPath(contractToUse));
        } else {
          setLeavePolicyUrl(`${HANDBOOK_PATH}/leave-policy-belarus.md`);
        }
      } catch (error) {
        setLeavePolicyUrl('');
      } finally {
        setIsLoading(false);
      }
    };

    determineLeavePolicyUrl();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { leavePolicyUrl, isLoading };
};

export default useLeavePolicyUrl;
